// The header's signed-in account menu, headless (registration plan step 2).
// Each template draws its own trigger and panel; this owns the behaviour:
// closes on Escape (focus back to the trigger), on a mousedown outside the
// trigger and the panel, and on navigation.

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

export function useAccountMenu<P extends HTMLElement = HTMLDivElement, T extends HTMLElement = HTMLButtonElement>() {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<T>(null)
  const panelRef = useRef<P>(null)
  const panelId = useId()
  const { pathname } = useLocation()

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = useCallback(() => setOpen((o) => !o), [])
  return { open, setOpen, toggle, triggerRef, panelRef, panelId }
}
